import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import PrimaryButton from "../../shared/PrimaryButton";
import { fetchStripeDashboardLink, fetchOrdersToday, fetchMonthlyRevenue, fetchRecentOrders } from "../../../util/admin/httpAdmin";
import LoadingIndicatorButton from "../../shared/LoadingIndicatorButton";
import "./HomeDashboardStepsCompletedAdmin.css";

//const rootUrl = `http://localhost:3000`;
const rootUrl = `https://resto-online.be`;
const HomeDashboardStepsCompletedAdmin = ({ subdomain }) => {
   const {
      data: stripeDashboardLink,
      isLoading: isLoadingStripe,
      isError: isErrorStripe,
   } = useQuery({
      queryKey: ["stripeDashboardLink", subdomain],
      queryFn: fetchStripeDashboardLink,
      enabled: !!subdomain, // Alleen fetchen als subdomain bestaat
   });
   
   // Fetch today's orders for pickup
   const {
      data: todayOrders,
      isLoading: isLoadingTodayOrders,
      isError: isErrorTodayOrders,
   } = useQuery({
      queryKey: ["orders", "today"],
      queryFn: fetchOrdersToday,
   });

   // Fetch monthly revenue
   const {
      data: monthlyRevenue,
      isLoading: isLoadingRevenue,
      isError: isErrorRevenue,
   } = useQuery({
      queryKey: ["revenue", "monthly"],
      queryFn: fetchMonthlyRevenue,
   });

   // Fetch recent orders
   const {
      data: recentOrders,
      isLoading: isLoadingRecentOrders,
      isError: isErrorRecentOrders,
   } = useQuery({
      queryKey: ["orders", "recent"],
      queryFn: fetchRecentOrders,
   });

   // Format currency
   const formatCurrency = (amount) => {
      return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(amount);
   };

   // Get current month name
   const getCurrentMonthName = () => {
      const months = [
         "januari", "februari", "maart", "april", "mei", "juni",
         "juli", "augustus", "september", "oktober", "november", "december"
      ];
      const currentMonth = new Date().getMonth();
      return months[currentMonth];
   };

   return (
      <div className="dashboard-overview g-30">
         <div className="dashboard-header">
            <h2>
               Je online winkel is zichtbaar op:{" "}
               <a
                  className="link"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`http://${rootUrl.split('//')[1]}/${subdomain}`}
               >
                  resto-online.be/{subdomain}
               </a>
            </h2>
         </div>

         <div className="dashboard-grid">
            {/* Today's Orders Section */}
            <div className="dashboard-card">
               <h3>Vandaag op te halen</h3>
               {isLoadingTodayOrders ? (
                  <LoadingIndicatorButton />
               ) : isErrorTodayOrders ? (
                  <p className="error-text">Er is een fout opgetreden bij het ophalen van de bestellingen.</p>
               ) : (
                  <div className="orders-list">
                     {todayOrders && todayOrders.length > 0 ? (
                        <div>
                           <p className="orders-count">{todayOrders.length} bestellingen</p>
                           <ul className="today-orders-list">
                              {todayOrders.map(order => (
                                 <li key={order._id} className={`order-item ${order.markedAsPickedUp ? 'picked-up' : ''}`}>
                                    <div className="order-time">
                                       {typeof order.timeSlot === 'object' 
                                          ? `${order.timeSlot.start} - ${order.timeSlot.end}`
                                          : order.timeSlot}
                                    </div>
                                    <div className="order-name">{order.customerDetails.name}</div>
                                    <div className="order-amount">{formatCurrency(order.totalAmount)}</div>
                                    <div className="order-status">
                                       {order.markedAsPickedUp ? (
                                          <span className="status-badge picked-up">Opgehaald</span>
                                       ) : (
                                          <span className="status-badge pending">Nog op te halen</span>
                                       )}
                                    </div>
                                 </li>
                              ))}
                           </ul>
                           <div className="view-all-link">
                              <a href="/dashboard/bestellingen" className="link">Alle bestellingen bekijken →</a>
                           </div>
                        </div>
                     ) : (
                        <p>Geen bestellingen voor vandaag.</p>
                     )}
                  </div>
               )}
            </div>

            {/* Monthly Revenue Section */}
            <div className="dashboard-card">
               <h3>Omzet {getCurrentMonthName()}</h3>
               {isLoadingRevenue ? (
                  <LoadingIndicatorButton />
               ) : isErrorRevenue ? (
                  <p className="error-text">Er is een fout opgetreden bij het ophalen van de omzetgegevens.</p>
               ) : (
                  <div className="revenue-stats">
                     {monthlyRevenue ? (
                        <div>
                           <div className="revenue-amount">{formatCurrency(monthlyRevenue.total)}</div>
                           <div className="revenue-details">
                              <div className="revenue-detail">
                                 <span className="detail-label">Aantal bestellingen:</span>
                                 <span className="detail-value">{monthlyRevenue.orderCount}</span>
                              </div>
                              <div className="revenue-detail">
                                 <span className="detail-label">Gemiddeld bedrag:</span>
                                 <span className="detail-value">{formatCurrency(monthlyRevenue.averageOrderValue)}</span>
                              </div>
                           </div>
                           {isLoadingStripe ? (
                              <p>Dashboard-link wordt geladen...</p>
                           ) : isErrorStripe || !stripeDashboardLink ? (
                              <p className="error-text">Kon Stripe dashboard niet laden.</p>
                           ) : (
                              <div className="stripe-link">
                                 <PrimaryButton onClick={() => window.open(stripeDashboardLink.url, "_blank")}>
                                    Details bekijken in Stripe
                                 </PrimaryButton>
                              </div>
                           )}
                        </div>
                     ) : (
                        <p>Geen omzetgegevens beschikbaar voor deze maand.</p>
                     )}
                  </div>
               )}
            </div>

            {/* Recent Orders Section */}
            <div className="dashboard-card">
               <h3>Recent binnengekomen</h3>
               {isLoadingRecentOrders ? (
                  <LoadingIndicatorButton />
               ) : isErrorRecentOrders ? (
                  <p className="error-text">Er is een fout opgetreden bij het ophalen van recente bestellingen.</p>
               ) : (
                  <div className="recent-orders">
                     {recentOrders && recentOrders.length > 0 ? (
                        <div>
                           <ul className="recent-orders-list">
                              {recentOrders.map(order => (
                                 <li key={order._id} className="recent-order-item">
                                    <div className="order-time-received">
                                       {new Date(order.createdAt).toLocaleTimeString('nl-BE', {
                                          hour: '2-digit',
                                          minute: '2-digit'
                                       })}
                                    </div>
                                    <div className="order-date">
                                       {new Date(order.date).toLocaleDateString('nl-BE', {
                                          day: 'numeric',
                                          month: 'numeric'
                                       })}
                                    </div>
                                    <div className="order-name">{order.customerDetails.name}</div>
                                    <div className="order-amount">{formatCurrency(order.totalAmount)}</div>
                                 </li>
                              ))}
                           </ul>
                           <div className="view-all-link">
                              <a href="/admin/bestellingen" className="link">Alle bestellingen bekijken →</a>
                           </div>
                        </div>
                     ) : (
                        <p>Geen recente bestellingen.</p>
                     )}
                  </div>
               )}
            </div>
         </div>
      </div>
   );
};

export default HomeDashboardStepsCompletedAdmin;
